<template>
  <div class="registration-page">
    <Header />
    <FileReport />

    <!-- Loading Overlay -->
    <div v-if="isRegistering" class="loading-overlay">
      <div class="loading-container">
        <div class="arrow-loader">
          <img src="/elevated_load.png" alt="" class="arrow-base" />
          <img src="/elevated_arrow.png" alt="" class="arrow-wipe" />
        </div>
        <p class="loading-text">Creating your account...</p>
      </div>
    </div>

    <div class="student-register-wrapper">
      <!-- Progress Indicator (Hidden during email verification) -->
      <div v-if="currentStep !== 'email-verification'" class="progress-header">
        <div class="header-content">
          <div class="title-section">
            <!-- Back Button -->
            <div class="back-button" @click="goBack">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="size-6"
              >
                <path
                  fill-rule="evenodd"
                  d="M11.03 3.97a.75.75 0 0 1 0 1.06l-6.22 6.22H21a.75.75 0 0 1 0 1.5H4.81l6.22 6.22a.75.75 0 1 1-1.06 1.06l-7.5-7.5a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 0 1 1.06 0Z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>

            <div class="title-content">
              <h1 class="main-title">Student Registration</h1>
              <p class="subtitle">
                Join our learning community and get the help you need to succeed!
              </p>
            </div>
          </div>

          <!-- Step Progress (now 3 steps) -->
          <div class="step-progress">
            <div
              class="step"
              :class="{
                active: currentStep === 'student-info-academic',
                completed: currentStep !== 'student-info-academic',
              }"
            >
              <span class="step-number">1</span>
            </div>
            <div class="step-line"></div>
            <div
              class="step"
              :class="{
                active: currentStep === 'subjects',
                completed: currentStep === 'subjects' || currentStep === 'verification',
              }"
            >
              <span class="step-number">2</span>
            </div>
            <div class="step-line"></div>
            <div class="step" :class="{ active: currentStep === 'verification' }">
              <span class="step-number">3</span>
            </div>
          </div>
        </div>
      </div>

      <div class="registration-content">
        <!-- Student Info & Academic Background Step (Combined) -->
        <StudentInfoAcademic
          v-if="currentStep === 'student-info-academic'"
          v-model="studentInfoAcademicData"
          @student-info-academic-complete="handleStudentInfoAcademicComplete"
        />

        <!-- Subject Selection Step -->
        <SubjectSelection
          v-if="currentStep === 'subjects'"
          v-model="subjectData"
          @subjects-complete="handleSubjectsComplete"
          @go-back="handleGoBack"
        />

        <!-- Student ID Verification Step -->
        <StudentIDVer
          v-if="currentStep === 'verification'"
          v-model="verificationData"
          @verification-complete="handleVerificationComplete"
          @go-back="handleGoBack"
        />

        <!-- Email Verification Step -->
        <EmailVerification
          v-if="currentStep === 'email-verification'"
          v-model="emailVerificationData"
          @verification-complete="handleEmailVerificationComplete"
          @change-email="handleChangeEmail"
        />
      </div>
    </div>

    <Footer />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from "vue";
import StudentInfoAcademic from "@/components/StudentInfoAcademic.vue";
import FileReport from "@/components/FileReport.vue";

import SubjectSelection from "@/components/SubjectSelection.vue";
import StudentIDVer from "@/components/StudentIDVer.vue";
import EmailVerification from "@/components/EmailVerification.vue";
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import { db } from "@/firebase";
import { doc, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  onAuthStateChanged,
} from "firebase/auth";
import { auth } from "@/firebase";
import { useRouter } from "vue-router";
import { useStudentRegistrationStorage } from "@/composables/useRegistrationStorage";
import { logDebug, logInfo, logError } from "@/utils/secureLogger";

const router = useRouter();

// Initialize registration storage
const { loadStudentInfo, loadStudentSubjects, loadStudentVerification, clearStudentRegistration } =
  useStudentRegistrationStorage();

onMounted(() => {
  document.title = "ElevatED – Student Registration";

  // Load saved data from sessionStorage
  const savedInfo = loadStudentInfo();
  const savedSubjects = loadStudentSubjects();
  const savedVerification = loadStudentVerification();

  if (savedInfo) {
    logDebug("Restored student info from sessionStorage");
    studentInfoAcademicData.value = savedInfo as any; // Will be populated with password by user
  }

  if (savedSubjects) {
    logDebug("Restored student subjects from sessionStorage");
    subjectData.value = savedSubjects;
  }

  if (savedVerification) {
    logDebug("Restored student verification data from sessionStorage");
    verificationData.value = {
      ...verificationData.value,
      termsAccepted: savedVerification.termsAccepted,
      // Note: Files cannot be restored from storage, user must re-upload
    };
  }
});

// Typesadd t
interface StudentInfoAcademicData {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  email: string;
  dateOfBirth: string;
  password: string;
  school: string;
  gradeLevel: string;
  academicTrack: string;
  isGoogleUser?: boolean;
}

interface SubjectData {
  selectedSubjects: string[];
}

interface VerificationData {
  studentIDFile: File | null;
  studentHoldingIdFile: File | null;
  termsAccepted: boolean;
  downloadURL?: string;
}

interface EmailVerificationData {
  verificationCode: string;
  email: string;
}

// Current step in the registration process (4 steps total)
const currentStep = ref<
  "student-info-academic" | "subjects" | "verification" | "email-verification"
>("student-info-academic");

// Loading state for registration
const isRegistering = ref(false);

// Store combined student info and academic data
const studentInfoAcademicData = ref<StudentInfoAcademicData | null>(null);

// Store subject data
const subjectData = ref<SubjectData>({
  selectedSubjects: [],
});

// Store verification data
const verificationData = ref<VerificationData>({
  studentIDFile: null,
  studentHoldingIdFile: null,
  termsAccepted: false,
});

// Store email verification data
const emailVerificationData = ref<EmailVerificationData>({
  verificationCode: "",
  email: "",
});

// SPA Scroll Management - Proper solution for Single Page Applications
const scrollToTop = async () => {
  // Method 1: Immediate scroll (for instant feedback)
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  
  // Method 2: Wait for Vue to finish rendering, then scroll again
  await nextTick();
  window.scrollTo({ top: 0, behavior: 'instant' });
  
  // Method 3: Final fallback after a small delay (handles any remaining async operations)
  setTimeout(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
  }, 10);
};

// Watch for step changes and automatically scroll to top (SPA solution)
watch(currentStep, async (newStep, oldStep) => {
  if (newStep !== oldStep) {
    logDebug(`Step changed from ${oldStep} to ${newStep} - scrolling to top`);
    await scrollToTop();
  }
}, { immediate: false, flush: 'post' });

// Watch for student info academic changes
watch(studentInfoAcademicData, (newData) => {
  if (newData && newData.email) {
    logDebug("Student email updated");
  }
});

// Handle student info and academic completion
const handleStudentInfoAcademicComplete = (data: StudentInfoAcademicData) => {
  logDebug("StudentRegisterView: Received student-info-academic-complete event");
  studentInfoAcademicData.value = data;
  logDebug('StudentRegisterView: Setting currentStep to "subjects"');
  
  currentStep.value = "subjects";
  logDebug("StudentRegisterView: Current step is now:", currentStep.value);
  
  // Scroll will be handled automatically by the watcher
};

// Handle subject selection completion
const handleSubjectsComplete = (subjects: SubjectData) => {
  // Store subject data and move to next step
  subjectData.value = { ...subjects };
  
  currentStep.value = "verification";
  
  // Scroll will be handled automatically by the watcher
};

// Handle verification completion
const handleVerificationComplete = async (verification: VerificationData) => {
  // Store verification data and create Firebase user
  verificationData.value = { ...verification };

  try {
    isRegistering.value = true; // Start loading
    logInfo("Verification completed, creating Firebase user...");

    // Create Firebase user and store all data
    const firebaseUid = await createFirebaseUserAndStoreData();

    // Update Firestore with registration completion
    await updateDoc(doc(db, "users", firebaseUid), {
      isEmailVerified: false, // Will be true after user clicks email link
      updatedAt: new Date(),
      registrationCompleted: true,
    });

    // Set up listener to update isVerified when user verifies their email
    setupEmailVerificationListener(firebaseUid);

    logInfo("Registration completed successfully!");
    logInfo("Verification email sent! User must click the link to verify their email.");

    // Clear sessionStorage as registration is complete
    clearStudentRegistration();

    // Redirect to email verification page
    router.push({
      path: "/email-verification",
      query: {
        email: studentInfoAcademicData.value?.email,
        from: "student-register",
      },
    });
  } catch (error: any) {
    logError("Error completing registration:", error);
    
    // Enhanced error logging
    console.error("FULL ERROR OBJECT:", error);
    console.error("ERROR CODE:", error.code);
    console.error("ERROR MESSAGE:", error.message);
    if (error.customData) {
      console.error("ERROR CUSTOM DATA:", error.customData);
    }

    // Handle specific Firebase errors
    if (error.code === "auth/email-already-in-use") {
      alert(
        "An account with this email already exists. Please use a different email or try logging in."
      );
    } else if (error.code === "auth/weak-password") {
      alert("Password is too weak. Please choose a stronger password (minimum 6 characters).");
    } else if (error.code === "auth/invalid-email") {
      alert("Please enter a valid email address.");
    } else if (error.code === "auth/missing-email") {
      alert("Email address is required.");
    } else if (error.code === "auth/missing-password") {
      alert("Password is required.");
    } else {
      alert(`Registration failed: ${error.message || "Please try again."}`);
    }
    
    // Stop loading and prevent navigation
    isRegistering.value = false;
    return; // Exit early to prevent any further execution
  } finally {
    isRegistering.value = false; // Stop loading
  }
};

// Create Firebase user and store all data
const createFirebaseUserAndStoreData = async () => {
  if (!studentInfoAcademicData.value) {
    throw new Error("Student data not available");
  }

  try {
    logInfo("Creating Firebase user");
    
    // Debug: Log the email and password length (not the actual password)
    console.log("DEBUG - Email:", studentInfoAcademicData.value.email);
    console.log("DEBUG - Password length:", studentInfoAcademicData.value.password?.length || 0);
    console.log("DEBUG - Email type:", typeof studentInfoAcademicData.value.email);
    console.log("DEBUG - Password type:", typeof studentInfoAcademicData.value.password);

    // Create user with email and password
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      studentInfoAcademicData.value.email,
      studentInfoAcademicData.value.password
    );
    const user = userCredential.user;
    logInfo("Firebase user created");

    // Send email verification
    await sendEmailVerification(user);
    logInfo("Email verification sent");

    // Upload student ID file to Firebase Storage if it exists
    let studentIDDownloadURL = "";
    if (verificationData.value.studentIDFile) {
      try {
        logInfo("Uploading student ID file to Firebase Storage...");

        // Import Firebase Storage functions
        const { storage } = await import("@/firebase");
        const { ref: storageRef, uploadBytes, getDownloadURL } = await import("firebase/storage");

        // Create a unique filename
        const timestamp = Date.now();
        const fileExtension = verificationData.value.studentIDFile.name.split(".").pop();
        const fileName = `student-id-${user.uid}-${timestamp}.${fileExtension}`;

        // Create storage reference
        const storageReference = storageRef(storage, `student-ids/${user.uid}/${fileName}`);

        // Upload file
        const snapshot = await uploadBytes(storageReference, verificationData.value.studentIDFile);

        // Get download URL
        studentIDDownloadURL = await getDownloadURL(snapshot.ref);
        logInfo("Student ID file uploaded successfully");
      } catch (uploadError) {
        logError("Error uploading student ID file:", uploadError);
        // Continue with registration even if file upload fails
      }
    }

    // Upload student holding ID file to Firebase Storage if it exists
    let studentHoldingIdDownloadURL = "";
    if (verificationData.value.studentHoldingIdFile) {
      try {
        logInfo("Uploading student holding ID file to Firebase Storage...");

        // Import Firebase Storage functions
        const { storage } = await import("@/firebase");
        const { ref: storageRef, uploadBytes, getDownloadURL } = await import("firebase/storage");

        // Create a unique filename
        const timestamp = Date.now();
        const fileExtension = verificationData.value.studentHoldingIdFile.name.split(".").pop();
        const fileName = `student-holding-id-${user.uid}-${timestamp}.${fileExtension}`;

        // Create storage reference
        const storageReference = storageRef(storage, `student-holding-ids/${user.uid}/${fileName}`);

        // Upload file
        const snapshot = await uploadBytes(
          storageReference,
          verificationData.value.studentHoldingIdFile
        );

        // Get download URL
        studentHoldingIdDownloadURL = await getDownloadURL(snapshot.ref);
        logInfo("Student holding ID file uploaded successfully");
      } catch (uploadError) {
        logError("Error uploading student holding ID file:", uploadError);
        // Continue with registration even if file upload fails
      }
    }

    // Store complete user data in Firestore
    const userData = {
      uid: user.uid,
      firstName: studentInfoAcademicData.value.firstName,
      middleName: studentInfoAcademicData.value.middleName,
      lastName: studentInfoAcademicData.value.lastName,
      suffix: studentInfoAcademicData.value.suffix,
      email: studentInfoAcademicData.value.email,
      dateOfBirth: studentInfoAcademicData.value.dateOfBirth,
      school: studentInfoAcademicData.value.school,
      gradeLevel: studentInfoAcademicData.value.gradeLevel,
      academicTrack: studentInfoAcademicData.value.academicTrack,
      userType: "student",
      isGoogleUser: studentInfoAcademicData.value.isGoogleUser || false,
      subjects: subjectData.value.selectedSubjects,
      isIDVerified: true,
      termsAccepted: verificationData.value.termsAccepted,
      // Store documents in nested structure (consistent with tutors)
      documents: {
        ...(verificationData.value.studentIDFile && {
          studentIDFile: {
            fileName: verificationData.value.studentIDFile.name,
            fileSize: verificationData.value.studentIDFile.size,
            fileType: verificationData.value.studentIDFile.type,
            downloadURL: studentIDDownloadURL,
            uploadedAt: new Date(),
          },
        }),
        ...(verificationData.value.studentHoldingIdFile && {
          studentHoldingIdFile: {
            fileName: verificationData.value.studentHoldingIdFile.name,
            fileSize: verificationData.value.studentHoldingIdFile.size,
            fileType: verificationData.value.studentHoldingIdFile.type,
            downloadURL: studentHoldingIdDownloadURL,
            uploadedAt: new Date(),
          },
        }),
      },
      isEmailVerified: false,
      isVerified: false, // Will be true after user clicks email verification link - controls login access
      status: "pending", // Initial status - admin will update to "active" or "verified" after review
      applicationStatus: "pending", // Application status for admin review
      profileStatus: "pending_approval", // Profile status for admin review
      applicationDate: new Date(), // When application was submitted
      registrationCompleted: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    logInfo("Storing complete user data in Firestore");
    await setDoc(doc(db, "users", user.uid), userData);
    logInfo("User data stored successfully in Firestore");

    return user.uid;
  } catch (error: any) {
    logError("Error creating Firebase user:", error);
    throw error;
  }
};

// Manual function to check and update verification status
const checkAndUpdateVerificationStatus = async (userId: string) => {
  try {
    logDebug("Manually checking verification status for user");

    if (auth.currentUser && auth.currentUser.uid === userId) {
      // Reload user to get the latest emailVerified status from Firebase Auth
      await auth.currentUser.reload();

      if (auth.currentUser.emailVerified) {
        logInfo("Email verified detected! Updating isVerified status in Firestore...");

        // Update Firestore with verified status
        await updateDoc(doc(db, "users", userId), {
          isEmailVerified: true,
          isVerified: true, // This controls login access
          emailVerifiedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        logInfo("User verification status updated successfully! isVerified = true");
        return true;
      } else {
        logDebug("Email not yet verified");
        return false;
      }
    }

    return false;
  } catch (error) {
    logError("Error checking verification status:", error);
    return false;
  }
};

// Set up listener to update isVerified when user verifies their email
const setupEmailVerificationListener = (userId: string) => {
  logDebug("Setting up email verification listener for user");

  // Set up a more robust checking mechanism
  let checkInterval: number;

  // Listen for auth state changes to detect when user verifies email
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user && user.uid === userId) {
      logDebug("Auth state changed - checking verification status...");

      // Check verification status
      const isVerified = await checkAndUpdateVerificationStatus(userId);

      if (isVerified) {
        logInfo("Verification complete! Cleaning up listeners...");
        // Clear interval and unsubscribe since verification is complete
        if (checkInterval) clearInterval(checkInterval);
        unsubscribe();
        return;
      }

      // Set up periodic checking every 3 seconds to catch verification
      if (!checkInterval) {
        logDebug("Setting up periodic verification check...");
        checkInterval = Number(
          setInterval(async () => {
            const verified = await checkAndUpdateVerificationStatus(userId);
            if (verified) {
              logInfo("Verification detected via periodic check!");
              clearInterval(checkInterval);
              unsubscribe();
            }
          }, 1000)
        ); // Check every 1 seconds
      }
    }
  });

  // Also do an immediate check in case user is already verified
  setTimeout(() => {
    checkAndUpdateVerificationStatus(userId);
  }, 1000);

  // Store unsubscribe function for cleanup if needed
  return () => {
    if (checkInterval) clearInterval(checkInterval);
    unsubscribe();
  };
};

// Handle back button click
const goBack = async () => {
  if (currentStep.value === "student-info-academic") {
    // If on first step, scroll then go back to role selection
    await scrollToTop();
    window.history.back();
  } else {
    // Go to previous step
    switch (currentStep.value) {
      case "subjects":
        currentStep.value = "student-info-academic";
        break;
      case "verification":
        currentStep.value = "subjects";
        break;
      case "email-verification":
        currentStep.value = "verification";
        break;
    }
    
    // Scroll will be handled automatically by the watcher
  }
};

// Handle go back from child components
const handleGoBack = () => {
  goBack();
};

// Handle email verification completion
const handleEmailVerificationComplete = (data: EmailVerificationData) => {
  logInfo("Email verification completed");
  // Handle email verification completion
  // You can add logic here to redirect to dashboard or show success message
};

// Handle change email request
const handleChangeEmail = () => {
  // Go back to student info step to change email
  currentStep.value = "student-info-academic";
};

// Initialize data when component mounts
onMounted(() => {
  // Ensure all data is properly initialized
  if (!subjectData.value.selectedSubjects) {
    subjectData.value = {
      selectedSubjects: [],
    };
  }

  if (
    !verificationData.value.studentIDFile &&
    !verificationData.value.studentHoldingIdFile &&
    !verificationData.value.termsAccepted
  ) {
    verificationData.value = {
      studentIDFile: null,
      studentHoldingIdFile: null,
      termsAccepted: false,
    };
  }
});
</script>

<style scoped>
.registration-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url("/student-reg-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
  width: 100%;
}

/* Remove background from wrapper since it's now on the page */
.student-register-wrapper {
  flex: 1;
  position: relative;
  width: 100%;
  padding-bottom: 40px; /* Add padding for footer */
  z-index: 0;
}

.registration-content {
  width: 100%;
  position: relative;
}

/* Override any existing backgrounds on child components to make them transparent */
:deep(.registration-container),
:deep(.content-wrapper),
:deep(.progress-section),
:deep(.verification-container),
:deep(.subject-selection-container),
:deep(.email-verification-container),
:deep(.header-section),
:deep(.student-id-ver-container),
:deep(.verification-card),
:deep(.main-content-card) {
  background: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  background-image: none !important;
  border: none !important;
  box-shadow: none !important;
}

/* Hide duplicate headers in child components */
:deep(.header-section),
:deep(.progress-container) {
  display: none !important;
}

.progress-header {
  padding: 40px 20px 0px;
  padding-top: 160px; /* Increased top padding to avoid nav bar overlap */
  text-align: center;
  position: relative;
  margin-bottom: 0px;
}

.header-content {
  max-width: 800px;
  margin: 0 auto;
}

.title-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5em;
  margin-right: 7em;
}

.back-button {
  width: 60px;
  height: 60px;
  background: white;
  border: 3px solid #000000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: none;
  color: #000000;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.back-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  color: #000000;
}

.back-button svg {
  width: 30px;
  height: 30px;
}

.title-content {
  text-align: center;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #000000;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.subtitle {
  font-size: 1.1rem;
  color: #333333;
  margin: 0 0 40px 0;
  font-style: italic;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.step-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  flex-wrap: wrap;
  margin: 0px 0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.step-number {
  width: 50px;
  height: 50px;
  border: 3px solid #000000;
  border-radius: 50%;
  background: #888888;
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  transition: all 0.3s ease;
  padding: 4px;
}

.step:first-child .step-number {
  background: #a2caff !important;
  color: #000000 !important;
  border-color: #000000 !important;
}

.step:first-child .step-number:hover {
  background: #a2caff !important;
  color: #000000 !important;
  border-color: #000000 !important;
}

.step.active .step-number {
  background: #a2caff;
  border-color: #000000;
  color: #000000;
}

/* Make step 2 blue when it's active (subjects step) */
.step:nth-child(3).active .step-number {
  background: #a2caff !important;
  border-color: #000000 !important;
  color: #000000 !important;
}

.step.completed .step-number {
  background: #a2caff;
  border-color: #000000;
  color: #000000;
}

.step-label {
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  text-align: center;
  max-width: 120px;
  line-height: 1.3;
}

.step.active .step-label {
  color: #000000;
  font-weight: 700;
}

.step.completed .step-label {
  color: #000000;
  font-weight: 600;
}

.step-line {
  width: 60px;
  height: 4px;
  background: #000000;
  margin: 0 8px;
  border-radius: 2px;
}

/* Form container styling to match design */
:deep(.form-container),
:deep(.form-card) {
  background: transparent !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 auto 40px !important;
  max-width: 1000px !important;
  border: none !important;
  margin-top: -550px !important;
}

/* Move subject selection container closer to step indicators */
:deep(.subject-selection-container) {
  margin-top: -30px !important;
}

:deep(.form-section) {
  background: white;
  padding: 32px;
  border-radius: 20px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 3px solid #090909;
  overflow: hidden;
}

:deep(.form-section-header) {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  padding: 16px;
  padding-bottom: 16px;
  border-bottom: none;
  background-color: transparent;
  border-radius: 0;
  margin: -32px -32px 16px -32px;
}

:deep(.form-section-header svg) {
  width: 28px;
  height: 28px;
  color: white;
}

:deep(.section-icon) {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

:deep(.form-section-title) {
  font-size: 20px;
  font-weight: 600;
  color: #1e40af !important;
}

:deep(.form-section-subtitle) {
  font-size: 14px;
  color: #3b82f6 !important;
  margin-top: 4px;
  font-style: normal;
}

.required {
  color: #dc2626;
  font-weight: 700;
}

:deep(.form-grid) {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 24px;
}

:deep(.form-input) {
  margin-bottom: 20px;
}

:deep(.form-label) {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

:deep(.input-field) {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fafafa;
}

:deep(.input-field:focus) {
  outline: none;
  border-color: #3861fb;
  box-shadow: 0 0 0 3px rgba(59, 97, 251, 0.1);
  background: white;
}

:deep(.submit-button) {
  background: #3861fb;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

:deep(.submit-button:hover) {
  background: #2a4bcc;
}

:deep(.submit-button svg) {
  width: 16px;
  height: 16px;
}

/* Responsive Design */
/* Style fixes for specific components */
:deep(.form-card),
:deep(.verification-card),
:deep(.subject-selection-card),
:deep(.email-verification-card),
:deep(.student-id-ver-container),
:deep(.main-content-card) {
  background: transparent !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 auto 40px !important;
  max-width: 1000px !important;
  border: none !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  background-image: none !important;
  background-color: transparent !important;
}

/* Loading Overlay Styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.arrow-loader {
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.arrow-base {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: 0.8;
}

.arrow-wipe {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: contain;
  animation: arrowWipe 2s ease-in-out infinite;
}

@keyframes arrowWipe {
  0% {
    clip-path: inset(100% 0 0 0);
    opacity: 1;
  }
  25% {
    clip-path: inset(0 0 0 0);
    opacity: 1;
  }
  75% {
    clip-path: inset(0 0 0 0);
    opacity: 1;
  }
  100% {
    clip-path: inset(0 0 100% 0);
    opacity: 0;
  }
}

.loading-text {
  font-size: 18px;
  font-weight: 500;
  color: #556fff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  text-align: center;
}

/* Responsive Styles */
@media (max-width: 768px) {
  .progress-header {
    padding: 30px 16px 20px;
    padding-top: 100px;
  }

  .title-section {
    gap: 16px;
    margin-bottom: 24px;
  }

  .back-button {
    width: 36px;
    height: 36px;
  }

  .back-button svg {
    width: 18px;
    height: 18px;
  }

  .main-title {
    font-size: 2rem;
    margin-bottom: 12px;
  }

  .subtitle {
    font-size: 1rem;
    margin-bottom: 24px;
  }

  .step-number {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .step-label {
    font-size: 11px;
    max-width: 70px;
  }

  .step-line {
    width: 30px;
  }

  :deep(.form-grid) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  :deep(.form-card),
  :deep(.verification-card),
  :deep(.subject-selection-card),
  :deep(.email-verification-card) {
    padding: 0 !important;
    margin: 0 16px 30px !important;
  }

  :deep(.form-section) {
    padding: 24px !important;
    margin-bottom: 20px !important;
  }
}

@media (max-width: 480px) {
  .student-register-wrapper {
    padding: 0 16px;
  }

  .progress-header {
    padding: 20px 12px 16px;
    padding-top: 80px;
  }

  .title-section {
    gap: 12px;
    margin-bottom: 20px;
  }

  .back-button {
    width: 32px;
    height: 32px;
  }

  .back-button svg {
    width: 16px;
    height: 16px;
  }

  .main-title {
    font-size: 1.75rem;
    margin-bottom: 8px;
  }

  .subtitle {
    font-size: 0.9rem;
    margin-bottom: 20px;
  }

  .step-progress {
    gap: 12px;
  }

  .step-number {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }

  .step-label {
    font-size: 10px;
    max-width: 60px;
  }

  .step-line {
    width: 20px;
  }

  :deep(.form-container) {
    padding: 20px 15px;
    border-radius: 10px;
  }
}
</style>
